import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import './AllTasks.css';
import LoadingIndicator from './LoadingIndicator';
import AppSidebar from './AppSidebar';
import { getInitials, getUserColor } from './tasklist';

/* --------------------------------------------------------------- constants */

const SOURCE_TABS = [
  { value: 'all', label: 'All Sources' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'delegation', label: 'Delegation' },
];

const DATE_FILTERS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'previous_week', label: 'Previous week' },
  { value: 'this_month', label: 'This month' },
  { value: 'previous_month', label: 'Previous month' },
  { value: 'custom', label: 'Custom range' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
];

const RECURRENCE_FILTERS = [
  { value: 'all', label: 'One time + recurring' },
  { value: 'recurring', label: 'Recurring only' },
  { value: 'one_time', label: 'One time only' },
];

const ACTIVE_FILTERS = [
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
  { value: 'all', label: 'Active + inactive' },
];

const PAGE_SIZES = [25, 50, 100, 250];

/* ----------------------------------------------------------------- helpers */

function todayString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  const dt = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const day = String(dt.getUTCDate()).padStart(2, '0');
  const mon = dt.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${day}-${mon}-${String(dt.getUTCFullYear()).slice(-2)}`;
}

// A collapsed task is judged on its most recent occurrence.
function taskState(row) {
  if (String(row.status || '').toLowerCase() === 'completed') return 'completed';
  if (row.latestPlannedDate && row.latestPlannedDate < todayString()) return 'overdue';
  return 'pending';
}

const STATE_LABEL = { completed: 'Completed', overdue: 'Overdue', pending: 'Pending' };

// Same judgement for a single occurrence inside the detail drawer.
function occurrenceState(item) {
  if (String(item.status || '').toLowerCase() === 'completed') return 'completed';
  if (item.plannedDate && item.plannedDate < todayString()) return 'overdue';
  return 'pending';
}

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function toCsvCell(value) {
  const text = value == null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/* ------------------------------------------------------------- tile icons  */

function TileIcon({ type }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  switch (type) {
    case 'total':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M3 9h18M9 20V9" />
        </svg>
      );
    case 'checklist':
      return (
        <svg {...common}>
          <path d="M9 11l2 2 4-4" />
          <rect x="4" y="4" width="16" height="16" rx="2" />
        </svg>
      );
    case 'delegation':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3.5 19a5.5 5.5 0 0111 0" />
          <path d="M16 8h5m-2.5-2.5L21 8l-2.5 2.5" />
        </svg>
      );
    case 'recurring':
      return (
        <svg {...common}>
          <path d="M7 7h4V3" />
          <path d="M17 17h-4v4" />
          <path d="M7 17A8 8 0 1017 7" />
        </svg>
      );
    case 'completed':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12.5l2.5 2.5L16 9.5" />
        </svg>
      );
    case 'pending':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3.5 2" />
        </svg>
      );
    case 'overdue':
      return (
        <svg {...common}>
          <path d="M10.3 3.9L1.9 18a2 2 0 001.7 3h16.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
          <path d="M12 9v4M12 17h.01" />
        </svg>
      );
    default:
      return null;
  }
}

function StatTile({ tone, icon, label, value, hint }) {
  return (
    <div className={`at-tile at-tile-${tone}`}>
      <span className="at-tile-label">
        <TileIcon type={icon} />
        {label}
      </span>
      <span className="at-tile-value">{value}</span>
      {hint ? <span className="at-tile-hint">{hint}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------- table setup */

const COLUMNS = [
  { key: 'uid', label: 'UID', sortable: true, get: (row) => row.uid },
  { key: 'title', label: 'Task', sortable: true, get: (row) => row.title },
  { key: 'source', label: 'Source', sortable: true, get: (row) => row.source },
  {
    key: 'frequency',
    label: 'Schedule',
    sortable: true,
    get: (row) =>
      `${row.frequency}${row.isRecurring && Number(row.recurringActive) === 0 ? ' stopped' : ''}`,
  },
  { key: 'assigneeName', label: 'Assigned to', sortable: true, get: (row) => row.assigneeName },
  { key: 'departmentName', label: 'Department', sortable: true, get: (row) => row.departmentName },
  { key: 'latestPlannedDate', label: 'Latest due', sortable: true, get: (row) => row.latestPlannedDate },
  { key: 'nextPlannedDate', label: 'Next due', sortable: true, get: (row) => row.nextPlannedDate },
  { key: 'occurrences', label: 'Occurrences', sortable: true, get: (row) => row.occurrences },
  { key: 'state', label: 'Status', sortable: true, get: (row) => STATE_LABEL[taskState(row)] },
];

/* --------------------------------------------------------------- component */

function AllTasks({
  user,
  onAuthError,
  onLogout,
  loggingOut = false,
  onNavigateToDashboard,
  onNavigateToTasks,
  onNavigateToFMS,
  onNavigateToReturns,
  onNavigateToDelegation,
  onNavigateToAllTasks,
  onNavigateToResourceControl,
  onNavigateToLogging,
}) {
  // Roles arrive as either numbers or numeric strings depending on the caller.
  const isAdminUser =
    Number(user?.role) === 1 ||
    Number(user?.role) === 2 ||
    String(user?.role).toLowerCase() === 'admin' ||
    String(user?.role).toLowerCase() === 'mis';

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({ employees: [], departments: [], frequencies: [] });
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filters
  const [source, setSource] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStart, setCustomStart] = useState(todayString());
  const [customEnd, setCustomEnd] = useState(todayString());
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [recurrenceFilter, setRecurrenceFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // table
  const [sort, setSort] = useState({ key: 'latestPlannedDate', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedRow, setSelectedRow] = useState(null);
  const selectedRowKey = selectedRow?.key || '';

  const searchTimer = useRef(null);

  useEffect(() => {
    document.title = 'All Tasks';
  }, []);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const loadTasks = useCallback(async () => {
    if (dateFilter === 'custom' && (!customStart || !customEnd)) return;

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        source,
        date_filter: dateFilter,
        status_filter: statusFilter,
        frequency_filter: frequencyFilter,
        recurrence_filter: recurrenceFilter,
        active_filter: activeFilter,
      });

      if (dateFilter === 'custom') {
        params.set('start_date', customStart);
        params.set('end_date', customEnd);
      }
      if (employeeFilter) params.set('employee_filter', employeeFilter);
      if (departmentFilter) params.set('department_filter', departmentFilter);
      if (search) params.set('search', search);

      const res = await fetch(`/api/all-tasks?${params.toString()}`, { credentials: 'include' });

      if (res.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Unable to load the centralised task list');
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setSummary(data.summary || null);
      setOptions(data.options || { employees: [], departments: [], frequencies: [] });
      setScope(data.scope || '');
    } catch (err) {
      setRows([]);
      setSummary(null);
      setError(err.message || 'Unable to load the centralised task list');
    } finally {
      setLoading(false);
    }
  }, [
    source,
    dateFilter,
    customStart,
    customEnd,
    employeeFilter,
    departmentFilter,
    frequencyFilter,
    statusFilter,
    recurrenceFilter,
    activeFilter,
    search,
    onAuthError,
  ]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Any filter change invalidates the current page window.
  useEffect(() => {
    setPage(1);
  }, [
    source,
    dateFilter,
    customStart,
    customEnd,
    employeeFilter,
    departmentFilter,
    frequencyFilter,
    statusFilter,
    recurrenceFilter,
    activeFilter,
    search,
    pageSize,
  ]);

  const sortedRows = useMemo(() => {
    const column = COLUMNS.find((col) => col.key === sort.key);
    if (!column) return rows;

    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => factor * compareValues(column.get(a), column.get(b)));
  }, [rows, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedRows, currentPage, pageSize]
  );

  const activeFilterCount = useMemo(() => {
    return [
      source !== 'all',
      dateFilter !== 'all',
      Boolean(employeeFilter),
      Boolean(departmentFilter),
      frequencyFilter !== 'all',
      statusFilter !== 'all',
      recurrenceFilter !== 'all',
      activeFilter !== 'active',
      Boolean(search),
    ].filter(Boolean).length;
  }, [
    source,
    dateFilter,
    employeeFilter,
    departmentFilter,
    frequencyFilter,
    statusFilter,
    recurrenceFilter,
    activeFilter,
    search,
  ]);

  useEffect(() => {
    if (!selectedRowKey) return;
    const refreshedRow = rows.find((row) => row.key === selectedRowKey);
    setSelectedRow(refreshedRow || null);
  }, [rows, selectedRowKey]);

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const handleExportCsv = () => {
    if (!sortedRows.length) return;

    const header = [
      'UID',
      'Source',
      'Task',
      'Description',
      'Frequency',
      'Recurring',
      'Recurring Active',
      'Assigned To',
      'Designation',
      'Assigned By',
      'Department',
      'First Planned Date',
      'Latest Due Date',
      'Next Due Date',
      'Delivered Date',
      'Status',
      'Occurrences',
      'Completed Occurrences',
      'Overdue Occurrences',
      'Week No',
      'Revisions',
      'Active',
    ];

    const body = sortedRows.map((row) => [
      row.uid,
      row.source,
      row.title,
      row.description,
      row.frequency,
      row.isRecurring ? 'Yes' : 'No',
      row.isRecurring ? (Number(row.recurringActive) === 0 ? 'Stopped' : 'Active') : 'No',
      row.assigneeName,
      row.assigneeDesignation,
      row.assignerName,
      row.departmentName,
      row.firstPlannedDate,
      row.latestPlannedDate,
      row.nextPlannedDate,
      row.deliveredDate,
      STATE_LABEL[taskState(row)],
      row.occurrences,
      row.completedCount,
      row.overdueCount,
      row.weekNo ?? '',
      row.revisionCount ?? '',
      Number(row.active) === 1 ? 'Yes' : 'No',
    ]);

    const csv = [header, ...body].map((line) => line.map(toCsvCell).join(',')).join('\r\n');
    // Leading BOM so Excel opens the file as UTF-8.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `all-tasks-${todayString()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleResetFilters = () => {
    setSource('all');
    setDateFilter('all');
    setCustomStart(todayString());
    setCustomEnd(todayString());
    setEmployeeFilter('');
    setDepartmentFilter('');
    setFrequencyFilter('all');
    setStatusFilter('all');
    setRecurrenceFilter('all');
    setActiveFilter('active');
    setSearchInput('');
  };

  return (
    <div className="app-shell">
      <AppSidebar
        user={user}
        active="allTasks"
        onLogout={onLogout}
        loggingOut={loggingOut}
        onNavigateToDashboard={onNavigateToDashboard}
        onNavigateToTasks={onNavigateToTasks}
        onNavigateToFMS={onNavigateToFMS}
        onNavigateToReturns={onNavigateToReturns}
        onNavigateToDelegation={onNavigateToDelegation}
        onNavigateToAllTasks={onNavigateToAllTasks}
        onNavigateToResourceControl={onNavigateToResourceControl}
        onNavigateToLogging={onNavigateToLogging}
      />

      <main className="workspace">
        <header className="fms-topbar rrl-topbar">
          <div className="mobile-left">
            <button className="mobile-menu-btn" type="button" aria-label="Open menu" />
          </div>

          <div className="fms-global-search at-top-search">
            <input
              type="search"
              placeholder="Search tasks by title, description or ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput ? (
              <button type="button" className="at-search-clear" onClick={() => setSearchInput('')} aria-label="Clear search">
                ×
              </button>
            ) : null}
          </div>

          <div className="topbar-actions">
            <div className="avatar-placeholder" style={{ background: getUserColor(user?.emp_name || user?.username) }}>
              {getInitials(user)}
            </div>
          </div>
        </header>

        <section className="at-page">
          <div className="at-header">
            <div>
              <h1>
                All Tasks
                <span className={`at-scope-chip ${scope === 'self' ? 'at-scope-self' : ''}`}>
                  {scope === 'self' ? 'My tasks' : 'All employees'}
                </span>
              </h1>
              <p>
                Every unique task across Checklist and Delegation. Recurring occurrences are rolled up
                into the task that created them, so nothing repeats in this list.
                {summary && summary.employeesCovered
                  ? ` Covering ${summary.employeesCovered} ${
                      summary.employeesCovered === 1 ? 'employee' : 'employees'
                    }.`
                  : ''}
              </p>
            </div>

            <div className="at-header-actions">
              <button type="button" className="at-btn" onClick={handleResetFilters} disabled={loading}>
                Reset filters
              </button>
              <button type="button" className="at-btn" onClick={loadTasks} disabled={loading}>
                Refresh
              </button>
              <button
                type="button"
                className="at-btn at-btn-primary"
                onClick={handleExportCsv}
                disabled={loading || !sortedRows.length}
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="at-kpi-row">
            <StatTile
              tone="total"
              icon="total"
              label="Unique tasks"
              value={summary ? summary.totalUnique.toLocaleString() : '-'}
              hint={summary ? `${summary.totalOccurrences.toLocaleString()} occurrences rolled up` : ''}
            />
            <StatTile
              tone="checklist"
              icon="checklist"
              label="Checklist"
              value={summary ? summary.checklist.toLocaleString() : '-'}
            />
            <StatTile
              tone="delegation"
              icon="delegation"
              label="Delegation"
              value={summary ? summary.delegation.toLocaleString() : '-'}
            />
            <StatTile
              tone="recurring"
              icon="recurring"
              label="Recurring"
              value={summary ? summary.recurring.toLocaleString() : '-'}
              hint={
                summary
                  ? `${summary.oneTime.toLocaleString()} one time${
                      summary.recurringPaused ? ` · ${summary.recurringPaused.toLocaleString()} stopped` : ''
                    }`
                  : ''
              }
            />
            <StatTile
              tone="completed"
              icon="completed"
              label="Completed"
              value={summary ? summary.completed.toLocaleString() : '-'}
            />
            <StatTile
              tone="pending"
              icon="pending"
              label="Pending"
              value={summary ? summary.pending.toLocaleString() : '-'}
            />
            <StatTile
              tone="overdue"
              icon="overdue"
              label="Overdue"
              value={summary ? summary.overdue.toLocaleString() : '-'}
            />
          </div>

          <div className="at-filters">
            <div className="at-field">
              <span className="at-field-label">Source</span>
              <div className="at-segment">
                {SOURCE_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    className={source === tab.value ? 'active' : ''}
                    onClick={() => setSource(tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {isAdminUser && (
              <div className="at-field">
                <span className="at-field-label">Employee</span>
                <select
                  className="at-select"
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                >
                  <option value="">All employees</option>
                  {options.employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="at-field">
              <span className="at-field-label">Department</span>
              <select
                className="at-select"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="">All departments</option>
                {options.departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="at-field">
              <span className="at-field-label">Frequency</span>
              <select
                className="at-select"
                value={frequencyFilter}
                onChange={(e) => setFrequencyFilter(e.target.value)}
              >
                <option value="all">All frequencies</option>
                {options.frequencies.map((freq) => (
                  <option key={freq} value={freq}>
                    {freq}
                  </option>
                ))}
              </select>
            </div>

            <div className="at-field">
              <span className="at-field-label">Status</span>
              <select className="at-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                {STATUS_FILTERS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="at-field">
              <span className="at-field-label">Recurrence</span>
              <select
                className="at-select"
                value={recurrenceFilter}
                onChange={(e) => setRecurrenceFilter(e.target.value)}
              >
                {RECURRENCE_FILTERS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="at-field">
              <span className="at-field-label">Task state</span>
              <select className="at-select" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
                {ACTIVE_FILTERS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="at-field">
              <span className="at-field-label">Date range</span>
              <select className="at-select" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                {DATE_FILTERS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            {dateFilter === 'custom' && (
              <>
                <div className="at-field">
                  <span className="at-field-label">From</span>
                  <input
                    className="at-input"
                    type="date"
                    value={customStart}
                    max={customEnd || undefined}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </div>
                <div className="at-field">
                  <span className="at-field-label">To</span>
                  <input
                    className="at-input"
                    type="date"
                    value={customEnd}
                    min={customStart || undefined}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="at-filter-summary">
              <strong>{sortedRows.length.toLocaleString()}</strong>
              <span>{sortedRows.length === 1 ? 'task' : 'tasks'}</span>
              <span>{activeFilterCount ? `${activeFilterCount} filters active` : 'No filters active'}</span>
            </div>
          </div>

          <div className="at-table-card">
            {loading ? (
              <div className="at-state">
                <LoadingIndicator variant="panel" label="Loading all tasks..." />
              </div>
            ) : error ? (
              <div className="at-state at-state-error">
                <strong>Could not load tasks</strong>
                <span>{error}</span>
                <button type="button" className="at-btn" onClick={loadTasks}>
                  Try again
                </button>
              </div>
            ) : !sortedRows.length ? (
              <div className="at-state">
                <strong>No tasks match these filters</strong>
                <span>Try widening the date range or clearing the search.</span>
                <button type="button" className="at-btn" onClick={handleResetFilters}>
                  Reset filters
                </button>
              </div>
            ) : (
              <>
                <div className="at-table-scroll">
                  <table className="at-table">
                    <thead>
                      <tr>
                        {COLUMNS.map((column) => (
                          <th
                            key={column.key}
                            className={`${column.sortable ? 'at-sortable' : ''} ${
                              sort.key === column.key ? 'at-sorted' : ''
                            }`}
                            onClick={column.sortable ? () => handleSort(column.key) : undefined}
                            aria-sort={
                              sort.key === column.key
                                ? sort.dir === 'asc'
                                  ? 'ascending'
                                  : 'descending'
                                : 'none'
                            }
                          >
                            {column.label}
                            {column.sortable && (
                              <span className="at-sort-caret">
                                {sort.key === column.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((row) => {
                        const state = taskState(row);
                        return (
                          <tr
                            key={row.key}
                            className={selectedRow?.key === row.key ? 'at-row-selected' : ''}
                            onClick={() => setSelectedRow(row)}
                          >
                            <td className="at-uid">{row.uid}</td>

                            <td className="at-task-cell">
                              <p className="at-task-title">{row.title}</p>
                              {row.description && row.description !== row.title ? (
                                <p className="at-task-desc">{row.description}</p>
                              ) : null}
                              <div className="at-task-meta">
                                {row.assignerName ? <span>By {row.assignerName}</span> : null}
                                {row.firstPlannedDate ? <span>First {formatDate(row.firstPlannedDate)}</span> : null}
                                {Number(row.active) !== 1 ? (
                                  <span className="at-inline-flag at-inline-flag-muted">Inactive</span>
                                ) : null}
                                {row.isRecurring && Number(row.recurringActive) === 0 ? (
                                  <span className="at-inline-flag at-inline-flag-warning">Recurring stopped</span>
                                ) : null}
                              </div>
                            </td>

                            <td>
                              <span
                                className={`at-badge ${
                                  row.source === 'Checklist' ? 'at-badge-checklist' : 'at-badge-delegation'
                                }`}
                              >
                                {row.source}
                              </span>
                            </td>

                            <td>
                              <span
                                className={`at-badge at-badge-freq ${
                                  row.isRecurring ? 'at-badge-recurring' : ''
                                } ${row.isRecurring && Number(row.recurringActive) === 0 ? 'at-badge-paused' : ''}`}
                              >
                                {row.isRecurring && Number(row.recurringActive) === 0
                                  ? `${row.frequency} · stopped`
                                  : row.frequency}
                              </span>
                            </td>

                            <td>
                              <div className="at-assignee">
                                <div
                                  className="at-avatar"
                                  style={{ background: getUserColor(row.assigneeName) }}
                                  title={row.assigneeName}
                                >
                                  {getInitials({ emp_name: row.assigneeName })}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div className="at-assignee-name">{row.assigneeName}</div>
                                  {row.assigneeDesignation ? (
                                    <div className="at-assignee-role">{row.assigneeDesignation}</div>
                                  ) : null}
                                </div>
                              </div>
                            </td>

                            <td>{row.departmentName || <span className="at-muted">-</span>}</td>

                            <td className="at-num">
                              {formatDate(row.latestPlannedDate) || <span className="at-muted">-</span>}
                            </td>
                            <td className="at-num">
                              {formatDate(row.nextPlannedDate) || <span className="at-muted">-</span>}
                            </td>

                            <td>
                              <span className="at-occ">
                                <span className={`at-occ-count ${row.occurrences > 1 ? 'at-occ-many' : ''}`}>
                                  {row.occurrences}
                                </span>
                                {row.completedCount ? (
                                  <span className="at-muted">{row.completedCount} done</span>
                                ) : null}
                              </span>
                            </td>

                            <td>
                              <span className={`at-badge at-badge-status-${state}`}>{STATE_LABEL[state]}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="at-table-foot">
                  <span className="at-pager-info">
                    Showing {(currentPage - 1) * pageSize + 1}-
                    {Math.min(currentPage * pageSize, sortedRows.length)} of {sortedRows.length} unique tasks
                  </span>

                  <div className="at-pager">
                    <select
                      className="at-select"
                      style={{ height: 26, minWidth: 96 }}
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      aria-label="Rows per page"
                    >
                      {PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size} / page
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={() => setPage(1)} disabled={currentPage === 1}>
                      First
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>
                    <span className="at-pager-info">
                      Page {currentPage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                    <button type="button" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>
                      Last
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {loggingOut ? (
          <div className="workspace-loader">
            <LoadingIndicator variant="panel" label="Logging out..." />
          </div>
        ) : null}
      </main>

      {selectedRow ? (
        <div className="at-drawer-backdrop" onClick={() => setSelectedRow(null)}>
          <aside className="at-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="at-drawer-head">
              <div>
                <span
                  className={`at-badge ${
                    selectedRow.source === 'Checklist' ? 'at-badge-checklist' : 'at-badge-delegation'
                  }`}
                >
                  {selectedRow.source} · {selectedRow.uid}
                </span>
                <h2>{selectedRow.title}</h2>
              </div>
              <button
                type="button"
                className="at-drawer-close"
                aria-label="Close task details"
                onClick={() => setSelectedRow(null)}
              >
                ×
              </button>
            </div>

            <div className="at-drawer-body">
              <div className="at-drawer-section">
                <h3>Description</h3>
                <p className="at-drawer-desc">
                  {selectedRow.description || 'No description recorded for this task.'}
                </p>
              </div>

              <div className="at-drawer-section">
                <h3>Key details</h3>
                <div className="at-kv">
                  <span>Status</span>
                  <strong>
                    <span className={`at-badge at-badge-status-${taskState(selectedRow)}`}>
                      {STATE_LABEL[taskState(selectedRow)]}
                    </span>
                  </strong>
                </div>
                <div className="at-kv">
                  <span>Frequency</span>
                  <strong>{selectedRow.frequency}</strong>
                </div>
                <div className="at-kv">
                  <span>Recurring</span>
                  <strong>
                    {selectedRow.isRecurring
                      ? Number(selectedRow.recurringActive) === 0
                        ? 'Yes, stopped'
                        : 'Yes, active'
                      : 'No'}
                  </strong>
                </div>
                <div className="at-kv">
                  <span>Assigned to</span>
                  <strong>
                    {selectedRow.assigneeName}
                    {selectedRow.assigneeDesignation ? ` (${selectedRow.assigneeDesignation})` : ''}
                  </strong>
                </div>
                <div className="at-kv">
                  <span>Assigned by</span>
                  <strong>{selectedRow.assignerName || '-'}</strong>
                </div>
                <div className="at-kv">
                  <span>Department</span>
                  <strong>{selectedRow.departmentName || '-'}</strong>
                </div>
                <div className="at-kv">
                  <span>Assigned date</span>
                  <strong>{formatDate(selectedRow.assignedDate) || '-'}</strong>
                </div>
                <div className="at-kv">
                  <span>First planned date</span>
                  <strong>{formatDate(selectedRow.firstPlannedDate) || '-'}</strong>
                </div>
                <div className="at-kv">
                  <span>Latest due date</span>
                  <strong>{formatDate(selectedRow.latestPlannedDate) || '-'}</strong>
                </div>
                {selectedRow.nextPlannedDate ? (
                  <div className="at-kv">
                    <span>Next due date</span>
                    <strong>{formatDate(selectedRow.nextPlannedDate)}</strong>
                  </div>
                ) : null}
                <div className="at-kv">
                  <span>Delivered date</span>
                  <strong>{formatDate(selectedRow.deliveredDate) || 'Delivery pending'}</strong>
                </div>
                {selectedRow.weekNo != null ? (
                  <div className="at-kv">
                    <span>Week no</span>
                    <strong>{selectedRow.weekNo}</strong>
                  </div>
                ) : null}
                {selectedRow.revisionCount != null ? (
                  <div className="at-kv">
                    <span>Revisions</span>
                    <strong>{selectedRow.revisionCount}</strong>
                  </div>
                ) : null}
                {selectedRow.source === 'Checklist' ? (
                  <div className="at-kv">
                    <span>Task active</span>
                    <strong>{Number(selectedRow.active) === 1 ? 'Yes' : 'No'}</strong>
                  </div>
                ) : null}
                {selectedRow.link ? (
                  <div className="at-kv">
                    <span>Link</span>
                    <strong>
                      <a href={selectedRow.link} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    </strong>
                  </div>
                ) : null}
              </div>

              <div className="at-drawer-section">
                <h3>
                  Occurrences ({selectedRow.occurrences}) · {selectedRow.completedCount} completed ·{' '}
                  {selectedRow.overdueCount} overdue
                </h3>
                <div className="at-history">
                  {(selectedRow.history || []).map((item) => (
                    <div className="at-history-row" key={`${selectedRow.key}-${item.id}`}>
                      <span className="at-history-uid">{item.uid}</span>
                      <span className="at-history-date">
                        {formatDate(item.plannedDate) || '-'}
                        {item.weekNo != null ? ` · Wk ${item.weekNo}` : ''}
                      </span>
                      <span className={`at-badge at-badge-status-${occurrenceState(item)}`}>
                        {occurrenceState(item) === 'overdue' ? 'Overdue' : item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

export default AllTasks;
